'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import type { MenuCategory } from '@/services/category.service';
interface MobileNavProps {
  categories: MenuCategory[];
}
const LEGACY_SHIELD_HUB_SLUG = 'scw-shield-alarm-hub-and-pin-pad-bundles.php';
const CANONICAL_SHIELD_HUB_SLUG = 'scw-shield-alarm-hub-and-pin-pad-bundles';
function normalizeLegacyMenuUrl(url: string): string {
  return url.replace(LEGACY_SHIELD_HUB_SLUG, CANONICAL_SHIELD_HUB_SLUG);
}

/**
 * Get the href for a menu category
 * Handles custom URLs, section headers (#), and external links
 */
function getCategoryHref(category: MenuCategory): string | null {
  const {
    customUrl,
    slug
  } = category;

  // No custom URL - use clean URL
  if (!customUrl) {
    return `/${slug}`;
  }
  const normalizedUrl = normalizeLegacyMenuUrl(customUrl);

  // Section header (no link)
  if (normalizedUrl === '#') {
    return null;
  }

  // External URL
  if (normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://')) {
    return normalizedUrl;
  }

  // Internal URL (starts with /)
  if (normalizedUrl.startsWith('/')) {
    return normalizedUrl;
  }

  // Fallback - treat as internal path
  return `/${normalizedUrl}`;
}

/**
 * Check if URL is external
 */
function isExternalUrl(url: string | null): boolean {
  if (!url) return false;
  return url.startsWith('http://') || url.startsWith('https://');
}
export function MobileNav(t0) {
  const $ = _c(36);
  const {
    categories
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [expandedCategories, setExpandedCategories] = useState(t1);
  let t2;
  if ($[1] !== expandedCategories) {
    t2 = categoryId => {
      const newExpanded = new Set(expandedCategories);
      if (newExpanded.has(categoryId)) {
        newExpanded.delete(categoryId);
      } else {
        newExpanded.add(categoryId);
      }
      setExpandedCategories(newExpanded);
    };
    $[1] = expandedCategories;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const toggleCategory = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsOpen(false);
      setExpandedCategories(new Set());
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const closeNav = t3;
  let t4;
  let t5;
  if ($[4] !== isOpen) {
    t4 = () => {
      if (!isOpen) {
        return;
      }
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    };
    t5 = [isOpen];
    $[4] = isOpen;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[7] !== isOpen) {
    t6 = () => {
      if (!isOpen) {
        return;
      }
      const desktop = window.matchMedia("(min-width: 64rem)");
      const handleChange = event => {
        if (event.matches) {
          closeNav();
        }
      };
      desktop.addEventListener("change", handleChange);
      return () => desktop.removeEventListener("change", handleChange);
    };
    t7 = [isOpen, closeNav];
    $[7] = isOpen;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setIsOpen(true);
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <button type="button" onClick={t8} className="flex h-9 w-9 items-center justify-center rounded-md border hover:bg-accent lg:hidden" aria-label="Open menu"><MenuIcon className="h-5 w-5" /></button>;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== isOpen) {
    t10 = isOpen && <div className="fixed inset-0 z-50 bg-black/50 lg:hidden" onClick={closeNav} />;
    $[12] = isOpen;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const t11 = isOpen ? "translate-x-0" : "-translate-x-full";
  let t12;
  if ($[14] !== t11) {
    t12 = cn("fixed inset-y-0 left-0 z-50 flex w-80 max-w-[85vw] transform flex-col bg-background shadow-xl transition-transform duration-300 ease-in-out lg:hidden", t11);
    $[14] = t11;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="text-lg font-semibold">Menu</span>;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="flex h-16 shrink-0 items-center justify-between border-b px-4">{t13}<button type="button" onClick={closeNav} className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-accent" aria-label="Close menu"><CloseIcon className="h-5 w-5" /></button></div>;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== categories || $[19] !== expandedCategories || $[20] !== toggleCategory) {
    let t16;
    if ($[22] !== expandedCategories || $[23] !== toggleCategory) {
      t16 = category => <MobileNavItem key={category.id} category={category} depth={0} isExpanded={expandedCategories.has(category.id)} onToggle={() => toggleCategory(category.id)} expandedCategories={expandedCategories} onToggleChild={toggleCategory} onClose={closeNav} />;
      $[22] = expandedCategories;
      $[23] = toggleCategory;
      $[24] = t16;
    } else {
      t16 = $[24];
    }
    t15 = categories.map(t16);
    $[18] = categories;
    $[19] = expandedCategories;
    $[20] = toggleCategory;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[25] !== t15) {
    t16 = <ul className="divide-y">{t15}</ul>;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="border-t p-4"><ul className="space-y-2"><li><Link href="/account" onClick={closeNav} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">My Account</Link></li><li><Link href="/checkout/cart" onClick={closeNav} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">Cart</Link></li><li><Link href="/contact-us" onClick={closeNav} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">Contact Us</Link></li><li><a href="tel:1-866-414-2725" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">1-866-414-2725</a></li></ul></div>;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t16) {
    t18 = <nav className="flex-1 overflow-y-auto overscroll-contain">{t16}{t17}</nav>;
    $[28] = t16;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t12 || $[31] !== t18) {
    t19 = <div className={t12}>{t14}{t18}</div>;
    $[30] = t12;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t10 || $[34] !== t19) {
    t20 = <>{t9}{t10}{t19}</>;
    $[33] = t10;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  return t20;
}
interface MobileNavItemProps {
  category: MenuCategory;
  depth: number;
  isExpanded: boolean;
  onToggle: () => void;
  expandedCategories: Set<number>;
  onToggleChild: (id: number) => void;
  onClose: () => void;
}
function MobileNavItem(t0) {
  const $ = _c(23);
  const {
    category,
    depth,
    isExpanded,
    onToggle,
    expandedCategories,
    onToggleChild,
    onClose
  } = t0;
  const hasChildren = category.children.length > 0;
  const paddingLeft = depth * 16 + 16;
  let href;
  let t1;
  if ($[0] !== category) {
    href = getCategoryHref(category);
    t1 = isExternalUrl(href);
    $[0] = category;
    $[1] = href;
    $[2] = t1;
  } else {
    href = $[1];
    t1 = $[2];
  }
  const isExternal = t1;
  let t2;
  if ($[3] !== category.name || $[4] !== hasChildren || $[5] !== href || $[6] !== isExpanded || $[7] !== isExternal || $[8] !== onClose || $[9] !== onToggle || $[10] !== paddingLeft) {
    t2 = <div className="flex items-center">{!href && hasChildren ? <button type="button" onClick={onToggle} aria-expanded={isExpanded} className="flex flex-1 items-center py-3 text-left text-sm hover:text-primary" style={{
        paddingLeft
      }}><span className="flex-1">{category.name}</span><span className="flex h-5 w-11 shrink-0 items-center justify-center"><ChevronDownIcon className={cn("h-4 w-4 transition-transform", isExpanded && "rotate-180")} /></span></button> : <>{href ? isExternal ? <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClose} className="flex-1 py-3 text-sm hover:text-primary" style={{
          paddingLeft
        }}>{category.name}</a> : <Link href={href} onClick={onClose} className="flex-1 py-3 text-sm hover:text-primary" style={{
          paddingLeft
        }}>{category.name}</Link> : <span className="flex-1 cursor-default py-3 text-sm" style={{
          paddingLeft
        }}>{category.name}</span>}{hasChildren && <button type="button" onClick={onToggle} aria-expanded={isExpanded} className="flex h-11 w-11 shrink-0 items-center justify-center hover:bg-accent" aria-label={isExpanded ? "Collapse" : "Expand"}><ChevronDownIcon className={cn("h-4 w-4 transition-transform", isExpanded && "rotate-180")} /></button>}</>}</div>;
    $[3] = category.name;
    $[4] = hasChildren;
    $[5] = href;
    $[6] = isExpanded;
    $[7] = isExternal;
    $[8] = onClose;
    $[9] = onToggle;
    $[10] = paddingLeft;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== category.children || $[13] !== depth || $[14] !== expandedCategories || $[15] !== hasChildren || $[16] !== isExpanded || $[17] !== onClose || $[18] !== onToggleChild) {
    t3 = hasChildren && isExpanded && <ul className="bg-muted/50">{category.children.map(child => <MobileNavItem key={child.id} category={child} depth={depth + 1} isExpanded={expandedCategories.has(child.id)} onToggle={() => onToggleChild(child.id)} expandedCategories={expandedCategories} onToggleChild={onToggleChild} onClose={onClose} />)}</ul>;
    $[12] = category.children;
    $[13] = depth;
    $[14] = expandedCategories;
    $[15] = hasChildren;
    $[16] = isExpanded;
    $[17] = onClose;
    $[18] = onToggleChild;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] !== t2 || $[21] !== t3) {
    t4 = <li>{t2}{t3}</li>;
    $[20] = t2;
    $[21] = t3;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  return t4;
}
function MenuIcon(t0) {
  const $ = _c(5);
  const {
    className
  } = t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <line x1="4" x2="20" y1="12" y2="12" />;
    t2 = <line x1="4" x2="20" y1="6" y2="6" />;
    t3 = <line x1="4" x2="20" y1="18" y2="18" />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== className) {
    t4 = <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{t1}{t2}{t3}</svg>;
    $[3] = className;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
function CloseIcon(t0) {
  const $ = _c(4);
  const {
    className
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M18 6 6 18" />;
    t2 = <path d="m6 6 12 12" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== className) {
    t3 = <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{t1}{t2}</svg>;
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function ChevronDownIcon(t0) {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="m6 9 6 6 6-6" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{t1}</svg>;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}